<script setup>
import { router, useForm } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import FormInput from '@/components/form/FormInput.vue'
import FormSelect from '@/components/form/FormSelect.vue'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

const props = defineProps({
  templates: Array,
  companies: Array,
})

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()
const fileUploadRef = ref(null)

const templateTypeOptions = [
  { value: 'EXPERT', label: t('messages.admin.template.type_expert') },
  { value: 'QUICK_VALUATION', label: t('messages.admin.template.type_quick_valuation') },
  // RED-356: the lühiaruanne for a direct (statistical-model) result. Offered
  // here regardless of `qv-statistical-report` — the mall has to be uploaded
  // before the flag is switched on for anyone.
  { value: 'QUICK_VALUATION_MODEL', label: t('messages.admin.template.type_quick_valuation_model') },
  { value: 'MAATUKK', label: t('messages.admin.template.type_maatukk') },
  { value: 'VORDLEVAD_TEHINGUD', label: t('messages.admin.template.type_vordlevad_tehingud') },
]

// RED-317: generated universal malls. The server lists them only while the
// market-overviews flag is on for this admin; their tags and switch sit in the
// description cell, so ordinary rows look as before. Only the switch is
// editable, and switching on needs every parity gate passed
// (`parity_state` = verified); switching an active one off always works.
const parityTags = {
  verified: { severity: 'success', label: 'messages.admin.template.parity_verified' },
  pending: { severity: 'warn', label: 'messages.admin.template.parity_pending' },
  failed: { severity: 'danger', label: 'messages.admin.template.parity_failed' },
}

const activationLocked = template => template.parity_state !== 'verified'

// The form posts through `router.post`, not `form.post`, so `form.errors` is
// never populated and FormInput/FormSelect can't render inline errors — the
// toast is the only channel the admin actually sees. RED-243's upload guards
// return specific, actionable Estonian text (which template to delete first,
// which placeholder is missing), so surface the server's own message instead
// of the generic fallback.
const firstErrorMessage = errors =>
  Object.values(errors ?? {})
    .flat()
    .find(message => typeof message === 'string' && message.length > 0)

const errorToast = (errors, fallbackDetail) => {
  const detail = firstErrorMessage(errors)

  toast.add({
    severity: 'error',
    summary: t('messages.error'),
    detail: detail ?? fallbackDetail,
    // Validation copy runs to two sentences — 3s is not enough to read it.
    life: detail ? 12000 : 3000,
  })
}

const form = useForm({
  description: null,
  company: null,
  type: 'EXPERT',
  file: null,
  filename: null,
})

const isLoading = ref(false)

const deleteTemplate = template => {
  confirm.require({
    message: t('messages.admin.template_delete_confirm'),
    header: t('messages.admin.template_delete_confirm_header'),
    accept: () => {
      isLoading.value = true
      router.delete(route('admin.template.destroy', template.id), {
        preserveScroll: true,
        onSuccess: () => {
          toast.add({
            severity: 'success',
            summary: t('messages.deleted'),
            detail: t('messages.admin.template_deleted_success_detail'),
            life: 3000,
          })
        },
        onError: () => {
          toast.add({
            severity: 'error',
            summary: t('messages.error'),
            detail: t('messages.admin.template_deleted_error_detail'),
            life: 3000,
          })
        },
        onFinish: () => {
          isLoading.value = false
        },
      })
    },
  })
}

const updateTemplate = data => {
  const update = {}
  update[data.field.key] = data.value

  // Optimistic write with rollback. A rejected update (RED-243 blocks retyping
  // into "Võrreldavate tehingute tabel" when the company already has one)
  // returns 422 without re-fetching props, so without the rollback the cell
  // would keep showing a type the server never accepted.
  const previousValue = data.item[data.field.key]
  data.item[data.field.key] = data.value

  isLoading.value = true

  router.put(route('admin.template.update', data.item.id), update, {
    preserveScroll: true,
    onSuccess: () => {
      toast.add({
        severity: 'success',
        summary: t('messages.updated'),
        detail: t('messages.admin.template_updated_success_detail'),
        life: 3000,
      })
    },
    onError: errors => {
      data.item[data.field.key] = previousValue
      errorToast(errors, t('messages.admin.template_updated_error_detail'))
    },
    onFinish: () => {
      isLoading.value = false
    },
  })
}

const addTemplate = () => {
  isLoading.value = true

  router.post(route('admin.template.store'), form, {
    preserveScroll: true,
    forceFormData: true,
    onSuccess: () => {
      form.reset()
      fileUploadRef.value.clear()
      toast.add({
        severity: 'success',
        summary: t('messages.added'),
        detail: t('messages.admin.template_added_success_detail'),
        life: 3000,
      })
    },
    onError: errors => {
      errorToast(errors, t('messages.admin.template_added_error_detail'))
    },
    onFinish: () => {
      isLoading.value = false
    },
  })
}

const fileSelected = evt => {
  if (evt.files.length === 1) {
    form.file = evt.files[0]
    form.filename = form.file.name
  }
}
</script>

<template>
  <div>
    <form class="mb-3 mt-3">
      <div class="flex gap-2">
        <FormInput
          v-model="form.description"
          :form="form"
          field="description"
          data-testid="template-description"
          :placeholder="$t('messages.admin.template.description')"
        />
        <FormSelect
          v-model="form.company"
          :form="form"
          field="company"
          data-testid="template-company"
          :placeholder="$t('messages.admin.select.company')"
          optionLabel="name"
          optionValue="id"
          :options="companies"
        />
        <FormSelect
          v-model="form.type"
          :form="form"
          field="type"
          data-testid="template-type"
          :placeholder="$t('messages.admin.template.type')"
          optionLabel="label"
          optionValue="value"
          :options="templateTypeOptions"
        />
        <FileUpload
          ref="fileUploadRef"
          mode="basic"
          data-testid="template-file"
          @select="fileSelected"
          :choose-label="$t('messages.admin.template.upload')"
        />
      </div>
      <Button
        class="px-4 mt-2"
        data-testid="template-add"
        @click.stop.prevent="addTemplate()"
        :disabled="!form.filename || !form.company || !form.description || isLoading"
      >
        {{ $t('messages.admin.template.add') }}
      </Button>
    </form>

    <Card :header="$t('messages.admin.templates')" class="mt-4">
      <template #content>
        <DataTable stripedRows hover :value="templates" data-testid="templates-table">
          <Column sortable field="filename" :header="$t('messages.admin.template.filename')">
            <template #body="slotProps">
              <a
                :href="route('admin.template.download', slotProps.data.id)"
                class="text-primary hover:underline"
                :title="$t('messages.admin.template.download_title')"
              >
                {{ slotProps.data.filename }}
              </a>
            </template>
          </Column>
          <Column sortable field="description" :header="$t('messages.admin.template.description')">
            <template #body="slotProps">
              {{ slotProps.data.description }}
              <div
                v-if="slotProps.data.is_universal"
                class="mt-1 flex flex-col gap-2"
                :data-testid="`template-universal-${slotProps.data.id}`"
              >
                <div class="flex flex-wrap gap-1">
                  <Tag :value="$t('messages.admin.template.universal')" severity="info" class="whitespace-nowrap" />
                  <Tag
                    :value="$t(parityTags[slotProps.data.parity_state].label)"
                    :severity="parityTags[slotProps.data.parity_state].severity"
                    class="whitespace-nowrap"
                    :data-testid="`template-parity-${slotProps.data.id}`"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <ToggleSwitch
                    :model-value="Boolean(slotProps.data.active)"
                    :disabled="activationLocked(slotProps.data) && !slotProps.data.active"
                    :input-id="`template-active-${slotProps.data.id}`"
                    :data-testid="`template-active-${slotProps.data.id}`"
                    @update:model-value="
                      updateTemplate({
                        item: slotProps.data,
                        field: { key: 'active' },
                        value: $event,
                      })
                    "
                  />
                  <label :for="`template-active-${slotProps.data.id}`" class="text-sm">
                    {{ $t('messages.admin.template.visible_to_evaluators') }}
                  </label>
                </div>
                <small
                  v-if="activationLocked(slotProps.data)"
                  class="text-xs text-gray-500"
                  :data-testid="`template-active-locked-${slotProps.data.id}`"
                >
                  {{ $t('messages.admin.template.activation_locked') }}
                </small>
              </div>
            </template>
          </Column>
          <Column sortable field="type" :header="$t('messages.admin.template.type')">
            <template #body="slotProps">
              <Select
                :model-value="slotProps.data.type"
                :options="templateTypeOptions"
                option-label="label"
                option-value="value"
                :disabled="slotProps.data.is_universal"
                @change="
                  updateTemplate({
                    item: slotProps.data,
                    field: { key: 'type' },
                    value: $event.value,
                  })
                "
              />
            </template>
          </Column>
          <Column sortable field="company.name" :header="$t('messages.admin.template.company')" />
          <Column sortable field="created_at" :header="$t('messages.admin.template.created')">
            <template #body="slotProps">
              {{ $filters.formatDateTime(slotProps.data.created_at) }}
            </template>
          </Column>
          <Column field="actions" header="Tegevused">
            <template #body="slotProps">
              <Button
                :disabled="isLoading"
                type="button"
                outlined
                severity="danger"
                :data-testid="`template-delete-${slotProps.data.id}`"
                @click="deleteTemplate(slotProps.data)"
              >
                {{ $t('messages.delete') }}
              </Button>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </div>
</template>
